import { 
  collection, 
  getDocs,
  doc,
  getDoc,
  updateDoc,
  DocumentSnapshot
} from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { validateHexCode, compareHexCodes } from '@/utils/codeValidator';
import { extractEmailFromUrl } from '@/utils/urlUtils';
import { createToken } from '@/utils/tokenManager';
import { VerificationResult, EvaluatorVerification } from '@/types/verification';
import { getAutomationById } from '@/services/automation';

async function findEvaluatorByEmail(email: string): Promise<DocumentSnapshot | null> {
  try {
    const evaluatorsRef = collection(db, collections.evaluators);
    const snapshot = await getDocs(evaluatorsRef);
    
    for (const doc of snapshot.docs) {
      const data = doc.data();
      if (data.email?.toLowerCase() === email.toLowerCase()) {
        return doc;
      }
    }
    return null;
  } catch (error) {
    console.error('Error finding evaluator:', error);
    throw error;
  }
}

export async function validateEvaluatorAccess(url: string): Promise<{
  isValid: boolean;
  evaluator?: EvaluatorVerification;
  message?: string;
}> {
  try {
    const email = extractEmailFromUrl(url);
    if (!email || !email.includes('@')) {
      return { isValid: false, message: 'Invalid email format' };
    }

    const evaluatorDoc = await findEvaluatorByEmail(email);
    if (!evaluatorDoc) {
      return { isValid: false, message: 'No evaluator found with this email' };
    }

    const evaluatorData = evaluatorDoc.data();
    if (!evaluatorData) {
      return { isValid: false, message: 'Invalid evaluator data' };
    }

    if (evaluatorData.evaluationCompleted) {
      return { isValid: false, message: 'This evaluation has already been completed' };
    }

    const userRef = doc(db, collections.users, evaluatorData.userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { isValid: false, message: 'Subject information not found' };
    }

    const userData = userDoc.data();
    const subjectName = `${userData.firstName} ${userData.lastName}`;

    return {
      isValid: true,
      evaluator: {
        id: evaluatorDoc.id,
        email: evaluatorData.email,
        uniqueCode: evaluatorData.uniqueCode,
        firstName: evaluatorData.firstName,
        lastName: evaluatorData.lastName,
        subjectName,
        evaluationCompleted: evaluatorData.evaluationCompleted
      }
    };
  } catch (error: any) {
    console.error('Error validating access:', error);
    return { 
      isValid: false, 
      message: error.message || 'Failed to validate access' 
    };
  }
}

export async function verifyCode(
  evaluatorId: string, 
  code: string
): Promise<VerificationResult> {
  try {
    if (!validateHexCode(code)) {
      return {
        isValid: false,
        message: 'Invalid verification code format'
      };
    }

    const evaluatorRef = doc(db, collections.evaluators, evaluatorId);
    const evaluatorDoc = await getDoc(evaluatorRef);

    if (!evaluatorDoc.exists()) {
      return {
        isValid: false,
        message: 'Evaluator not found'
      };
    }

    const evaluatorData = evaluatorDoc.data();
    const isValid = compareHexCodes(code, evaluatorData.uniqueCode);

    if (!isValid) {
      return {
        isValid: false,
        message: 'Invalid verification code'
      };
    }

    await updateDoc(evaluatorRef, {
      emailVerified: true,
      updatedAt: new Date()
    });

    const token = await createToken(evaluatorId);

    return {
      isValid: true,
      message: 'Verification successful',
      token
    };
  } catch (error: any) {
    console.error('Error verifying code:', error);
    return {
      isValid: false,
      message: 'Failed to verify code. Please try again.'
    };
  }
}

export async function sendVerificationCode(evaluator: EvaluatorVerification): Promise<void> {
  try {
    const automation = await getAutomationById('send-verification-code');
    if (!automation) {
      throw new Error('Verification service configuration not found');
    }

    const response = await fetch(automation.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: evaluator.email,
        uniqueCode: evaluator.uniqueCode,
        firstName: evaluator.firstName,
        lastName: evaluator.lastName,
        subjectName: evaluator.subjectName,
        action: 'send_code'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to send verification code');
    }
  } catch (error: any) {
    console.error('Error sending verification code:', error);
    throw new Error('Failed to send verification code. Please try again.');
  }
}